import { useSubmit } from "react-router";
import { useEffect, useState } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { useAuthenticityToken } from "remix-utils/csrf/react";

import { Input } from "./ui/input";

type Props = {
  value: string;
  subaction?: string;
  onChange?: (text: string) => void;
  Tag?: keyof JSX.IntrinsicElements;
  className?: string;
  testId?: string;
  focused?: boolean;
  otherInputs?: Record<string, string>;
};

export function InlineEditableText({
  value,
  subaction,
  testId,
  Tag = "span",
  className = "",
  focused = false,
  otherInputs = {},
  onChange,
}: Props) {
  const submit = useSubmit();
  const csrf = useAuthenticityToken();
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value);

  useEffect(() => {
    setIsEditing(focused);
    setText(value);
  }, [value, focused]);

  const handleBlur = () => {
    setIsEditing(false);
    if (text.length === 0) {
      return setText(value);
    }
    if (text !== value) {
      if (subaction) {
        submit({ subaction, text, csrf, ...otherInputs }, { method: "post" });
      } else if (onChange) {
        onChange(text);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Save the value and exit editing mode
      handleBlur();
      // Also blur the input to ensure it loses focus
      event.currentTarget.blur();
    }
  };

  const id = `inline-editable-text-${value}`;

  const getTextWidth = (text: string) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const element = document.getElementById(id);
    if (!element || !context) return 0;
    context.font = getComputedStyle(element).font;
    return context.measureText(text).width;
  };

  const tag = (
    <Tag
      id={id}
      onClick={() => setIsEditing(true)}
      className={`p-0 border border-dashed hover:ring-1 hover:ring-ring rounded-sm inline-block ${className}`}
      style={{
        paddingLeft: 2,
        paddingRight: 2,
      }}
      data-testid={testId}
    >
      {text}
    </Tag>
  );

  if (!isEditing) return tag;

  return (
    <ClientOnly fallback={tag}>
      {() => (
        <div className="cursor-text flex items-center">
          <Input
            id={id}
            className={`p-0 h-auto rounded-sm ${className}`}
            style={{
              width: `${getTextWidth(text) + 10}px`,
              marginLeft: 2,
            }}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onFocus={(e) => e.target.select()}
            data-testid={testId}
            data-inline-editable-text="true"
            autoFocus
          />
        </div>
      )}
    </ClientOnly>
  );
}
